// as-cetra-ui/src/components/authenticated/handout/customise-mode/CustomiseModeContext.js

import React, { createContext, useContext, useState } from 'react';

const CustomiseModeContext = createContext();

export const CustomiseModeProvider = ({ children }) => {
    const [isCustomiseMode, setIsCustomiseMode] = useState(false);
    const [editedContentData, setEditedContentData] = useState(null);
    const [editedContentHandoutId, setEditedContentHandoutId] = useState(null);
    const [editingBlockId, setEditingBlockId] = useState(null); // which one block is actually being edited
    const [isLoadedCustomisedHandout, setIsLoadedCustomisedHandout] = useState(false); // Track if CH was loaded from library
    const [selectedWhiteboard, setSelectedWhiteboard] = useState(null); // Track selected whiteboard for main illustration

    // Phase 1: Track active whiteboard drawings for combined save
    const [hasActiveWhiteboardDrawings, setHasActiveWhiteboardDrawings] = useState(false);
    const [activeCanvasRef, setActiveCanvasRef] = useState(null); // Reference to active WB canvas

    // Helper: Add stable React keys to all blocks if they don't have them
    const ensureBlockKeys = (contentData) => {
        if (!contentData || typeof contentData !== 'object') return contentData;

        const processed = JSON.parse(JSON.stringify(contentData));

        Object.keys(processed).forEach((locale) => {
            if (Array.isArray(processed[locale])) {
                processed[locale].forEach((section) => {
                    if (section.blocks && Array.isArray(section.blocks)) {
                        section.blocks.forEach((block, idx) => {
                            if (!block._reactKey) {
                                // Generate deterministic stable key based on block content + position
                                // Use block value/type to create content-based key (not random)
                                const valueHash = block.value
                                    ? String(block.value)
                                          .substring(0, 30)
                                          .replace(/[^a-zA-Z0-9]/g, '')
                                    : block.type || 'block';
                                block._reactKey = `block-${section.sectionName}-${idx}-${valueHash}`;
                            }
                        });
                    }
                });
            }
        });

        return processed;
    };

    // Initialize edited content from original handout
    const initializeCustomiseMode = (originalContentData, locale = 'en-UK', handoutId = null) => {
        // Deep clone original content - IMPORTANT: preserve existing _reactKey if present
        const cloned = JSON.parse(JSON.stringify(originalContentData));

        // Only add keys to blocks that don't already have them
        const withKeys = ensureBlockKeys({ [locale]: cloned });
        setEditedContentData(withKeys);
        setEditedContentHandoutId(handoutId);
        setIsCustomiseMode(true);
        // This is NOT a loaded CH, just temporary customise mode
        setIsLoadedCustomisedHandout(false);
    };

    // Load customised content in VIEW mode (for viewing saved CH without editing)
    const loadCustomisedContentInViewMode = (customisedContentData, locale = 'en-UK', handoutId = null) => {
        // Deep clone customised content and ensure all blocks have keys
        const cloned = JSON.parse(JSON.stringify(customisedContentData));
        const withKeys = ensureBlockKeys({ [locale]: cloned });
        setEditedContentData(withKeys);
        setEditedContentHandoutId(handoutId);
        setIsCustomiseMode(false); // View mode - toggle is OFF
        setIsLoadedCustomisedHandout(true); // Mark as loaded CH
    };

    // Load customised content directly into EDIT mode (e.g. restore pending save)
    const loadCustomisedContentForEdit = (customisedContentData, locale = 'en-UK', handoutId = null) => {
        const cloned = JSON.parse(JSON.stringify(customisedContentData));
        const hasLocaleKey = cloned && typeof cloned === 'object' && cloned[locale];
        const dataToSet = hasLocaleKey ? cloned : { [locale]: cloned };
        const withKeys = ensureBlockKeys(dataToSet);
        setEditedContentData(withKeys);
        setEditedContentHandoutId(handoutId);
        setIsCustomiseMode(true);
        setIsLoadedCustomisedHandout(false);
        setEditingBlockId(null);
    };

    // Update specific block
    const updateBlock = (sectionIndex, blockIndex, newBlockData) => {
        setEditedContentData((prev) => {
            const updated = JSON.parse(JSON.stringify(prev));
            const locale = Object.keys(updated)[0];
            // Preserve the _reactKey from the old block
            const oldBlock = updated[locale][sectionIndex].blocks[blockIndex];
            updated[locale][sectionIndex].blocks[blockIndex] = {
                ...newBlockData,
                _reactKey: oldBlock._reactKey || newBlockData._reactKey,
            };
            return updated;
        });
    };

    // Remove block
    const removeBlock = (sectionIndex, blockIndex) => {
        setEditedContentData((prev) => {
            const updated = JSON.parse(JSON.stringify(prev));
            const locale = Object.keys(updated)[0];
            updated[locale][sectionIndex].blocks.splice(blockIndex, 1);
            return updated;
        });
    };

    // Add new paragraph below
    const addParagraphBelow = (sectionIndex, blockIndex) => {
        setEditedContentData((prev) => {
            const updated = JSON.parse(JSON.stringify(prev));
            const locale = Object.keys(updated)[0];

            const newBlock = {
                type: 'paragraph',
                value: '', // Empty paragraph for user to type
                _reactKey: `block-${Date.now()}-${Math.random()}`, // Unique stable key for React rendering
            };

            // Insert after blockIndex
            updated[locale][sectionIndex].blocks.splice(blockIndex + 1, 0, newBlock);
            return updated;
        });
    };

    // Exit customise mode (switch from edit mode to view mode)
    const exitCustomiseMode = () => {
        setIsCustomiseMode(false);
        // DON'T clear editedContentData - we want to keep showing customised content in view mode
        // setEditedContentData(null); // REMOVED
        setEditingBlockId(null);
    };

    // Mark current handout as a saved customised handout (after save)
    const markAsLoadedCustomisedHandout = () => {
        setIsLoadedCustomisedHandout(true);
    };

    // Set selected whiteboard for main illustration
    const setWhiteboardIllustration = (whiteboardData) => {
        setSelectedWhiteboard(whiteboardData);
    };

    // Fully clear customise mode (used when leaving the handout entirely)
    const clearCustomiseMode = () => {
        setIsCustomiseMode(false);
        setEditedContentData(null);
        setEditedContentHandoutId(null);
        setEditingBlockId(null);
        setIsLoadedCustomisedHandout(false);
        setSelectedWhiteboard(null);
        setHasActiveWhiteboardDrawings(false);
        setActiveCanvasRef(null);
    };

    // Get current content (edited or original)
    const getCurrentContentData = (originalContentData, locale = 'en-UK', handoutId = null) => {
        // If we have edited content (either in view mode or edit mode), show it
        if (editedContentData && editedContentData[locale] && (!handoutId || editedContentHandoutId === handoutId)) {
            return editedContentData[locale];
        }
        // Otherwise, fallback to original content
        return originalContentData[locale];
    };

    // Get full edited content data (for saving)
    const getEditedContentData = () => {
        return editedContentData;
    };

    return (
        <CustomiseModeContext.Provider
            value={{
                isCustomiseMode,
                setIsCustomiseMode,
                editedContentData,
                editedContentHandoutId,
                editingBlockId,
                setEditingBlockId,
                isLoadedCustomisedHandout,
                selectedWhiteboard,
                setWhiteboardIllustration,
                initializeCustomiseMode,
                loadCustomisedContentInViewMode,
                loadCustomisedContentForEdit,
                updateBlock,
                removeBlock,
                addParagraphBelow,
                exitCustomiseMode,
                markAsLoadedCustomisedHandout,
                clearCustomiseMode,
                getCurrentContentData,
                getEditedContentData,
                // Phase 1: Combined mode support
                hasActiveWhiteboardDrawings,
                setHasActiveWhiteboardDrawings,
                activeCanvasRef,
                setActiveCanvasRef,
            }}
        >
            {children}
        </CustomiseModeContext.Provider>
    );
};

export const useCustomiseMode = () => {
    const context = useContext(CustomiseModeContext);
    if (!context) {
        throw new Error('useCustomiseMode must be used within CustomiseModeProvider');
    }
    return context;
};
